// useKeyboardShortcuts.ts - Hook para atalhos de teclado do Bigwa
// v5.1 - Janeiro 2026 - Removido atalho c-e (teoria-empiria)
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'wouter';

// Stores das janelas flutuantes
import { useTaskPanelStore } from '../stores/useTaskPanelStore';
import { useScratchpadStore } from '../stores/useScratchpadStore';
import { useOutlineStore } from '../stores/outlineStore';


interface ListNavigationActions {
  focusNext: () => void;
  focusPrev: () => void;
  openFocused: () => void;
  deleteFocused: () => void;
  toggleInspector: () => void;
  setInspectorTab: (tab: number) => void;
  hasList: boolean;
}

interface UseKeyboardShortcutsOptions {
  onShowHelp?: () => void;
  onGlobalSearch?: () => void;
  /** ⌘K — busca primária da página (Bigwa Search em Leituras; QuickFind nas demais).
   *  Sem este callback, ⌘K cai no onGlobalSearch (compat). */
  onPrimarySearch?: () => void;
  onNewTask?: () => void;
  onNewReference?: () => void;
  onShowFavoritos?: () => void;
  onSync?: () => void;
  onOpenPreferences?: () => void;
  onOpenBussola?: () => void;
  onEmptyTrash?: () => void;
  listActions?: ListNavigationActions;
  onToggleGlobalInspector?: () => void;
  onToggleSidebar?: () => void;
}

export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const [, setLocation] = useLocation();
  const [waitingForNavKey, setWaitingForNavKey] = useState(false);
  const [navSequence, setNavSequence] = useState<string[]>([]); // Para sequências G+C+T
  const [navTimeout, setNavTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Navegação simples: G + letra
  const navigationMap: Record<string, string> = {
    'h': '/',
    'b': '/library',
    'l': '/leituras',
    // Sprint 006 — G+O ('o': '/colaboracao') removido: Colaboração escondida (paridade Conexões; rota /colaboracao segue viva)
    'd': '/dados',
    'a': '/arquivo',
  };

  // Teclas exibidas no balão "Ir para:" (G + letra) — derivadas do navigationMap
  // para nunca ficarem stale. O balão era hardcoded e anunciou atalhos mortos
  // duas vezes (C/Conexões no Sprint 003, O/Colaboração no Sprint 006); a fonte
  // única elimina a classe do bug. Entrar/sair do navigationMap propaga ao balão.
  const navKeys = Object.keys(navigationMap).map((k) => k.toUpperCase());

  // Navegação por perspectiva: G + P + letra (para Preferências)
  // Sprint 003: removidas as entradas G+C+* (Conexões escondida — ver acceptance.md G3.2);
  // v5.1 já havia removido 'c-e' (teoria-empiria)
  const perspectiveMap: Record<string, string> = {
    'p-g': '/preferencias?tab=geral',       // G+P+G = Preferências Geral
    'p-a': '/preferencias?tab=acessibilidade', // G+P+A = Acessibilidade
    'p-t': '/preferencias?tab=anotacoes',   // G+P+T = Tags/Anotações
    'p-i': '/preferencias?tab=integracoes', // G+P+I = Integrações
    'p-c': '/preferencias?tab=conta',       // G+P+C = Conta
  };

  const isTyping = useCallback(() => {
    const active = document.activeElement;
    if (!active) return false;
    const tagName = active.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true;
    if ((active as HTMLElement).isContentEditable) return true;
    return false;
  }, []);

  const clearNavState = useCallback(() => {
    setWaitingForNavKey(false);
    setNavSequence([]);
    if (navTimeout) clearTimeout(navTimeout);
  }, [navTimeout]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!e.key) return;
    const key = e.key.toLowerCase();
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modKey = isMac ? e.metaKey : e.ctrlKey;

    // ============================================
    // ALT + 1/2/3 → JANELAS FLUTUANTES
    // Funciona mesmo em inputs (para acesso rápido)
    // ============================================
    if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      // Não bloquear em inputs - permite acesso rápido às janelas
      // mas verificar se não está em campo que usa Alt para caracteres especiais
      const activeEl = document.activeElement;
      const isInput = activeEl?.tagName === 'INPUT' || 
                      activeEl?.tagName === 'TEXTAREA' || 
                      (activeEl as HTMLElement)?.isContentEditable;
      
      if (!isInput) {
        if (e.key === '1') {
          e.preventDefault();
          useTaskPanelStore.getState().toggle();
          return;
        }
        if (e.key === '2') {
          e.preventDefault();
          useOutlineStore.getState().togglePanel();
          return;
        }
        if (e.key === '3') {
          e.preventDefault();
          useScratchpadStore.getState().toggle();
          return;
        }
      }
    }

    // Ignorar outros atalhos se estiver digitando
    if (isTyping()) return;

    // ⌘+, - Abrir Preferências (padrão macOS) - dispara evento global
    if (modKey && key === ',') {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('open-preferencias', { detail: { tab: 'geral' } }));
      return;
    }

    // ⌘+? (⌘+Shift+/) - Abrir Bússola
    if (modKey && e.shiftKey && key === '?') {
      e.preventDefault();
      options.onOpenBussola?.();
      return;
    }

    // ⌘+/ - Mostrar ajuda
    if (modKey && key === '/') {
      e.preventDefault();
      options.onShowHelp?.();
      return;
    }

    // ⌘+⇧+F - Favoritos
    if (modKey && e.shiftKey && key === 'f') {
      e.preventDefault();
      options.onShowFavoritos?.();
      return;
    }

    // ⌘+⇧+I - Toggle Global Inspector
    if (modKey && e.shiftKey && key === 'i') {
      e.preventDefault();
      return;
    }

    // ⌘+\ - Recolher/expandir barra lateral (par fechado: visível → oculta;
    // oculta → sempre expandida — decisão 3, registro 11/jun)
    if (modKey && key === '\\') {
      e.preventDefault();
      options.onToggleSidebar?.();
      return;
    }

    // ⌘+⇧+K - Busca global (QuickFind) — porta permanente do navegador
    if (modKey && e.shiftKey && key === 'k') {
      e.preventDefault();
      options.onGlobalSearch?.();
      return;
    }

    // ⌘+K - Busca primária da página (Bigwa Search em Leituras; QuickFind nas demais)
    if (modKey && key === 'k') {
      e.preventDefault();
      if (options.onPrimarySearch) {
        options.onPrimarySearch();
      } else {
        options.onGlobalSearch?.();
      }
      return;
    }

    // ⌘+T - Nova tarefa
    if (modKey && key === 't') {
      e.preventDefault();
      options.onNewTask?.();
      return;
    }

    // ⌘+N - Nova referência
    if (modKey && key === 'n') {
      if (window.location.pathname === '/library') {
        e.preventDefault();
        options.onNewReference?.();
      }
      return;
    }

    // ⌘+S - Sync
    if (modKey && key === 's') {
      e.preventDefault();
      options.onSync?.();
      return;
    }

    // ⌘+⇧+Backspace - Esvaziar Lixeira (global)
    if (modKey && e.shiftKey && (e.key === 'Backspace' || e.key === 'Delete')) {
      e.preventDefault();
      options.onEmptyTrash?.();
      return;
    }

    // ⌘+Backspace - Deletar
    if (modKey && (e.key === 'Backspace' || e.key === 'Delete')) {
      if (options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.deleteFocused();
      }
      return;
    }

    // J - Próximo
    if (key === 'j' && !modKey && !e.shiftKey && !e.altKey && !waitingForNavKey) {
      if (options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.focusNext();
      }
      return;
    }

    // K - Anterior
    if (key === 'k' && !modKey && !e.shiftKey && !e.altKey && !waitingForNavKey) {
      if (options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.focusPrev();
      }
      return;
    }

    // Enter - Abrir
    if (key === 'enter' && !modKey && !e.shiftKey && !e.altKey) {
      if (options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.openFocused();
      }
      return;
    }

    // I - Toggle Inspector
    if (key === 'i' && !modKey && !e.shiftKey && !e.altKey && !waitingForNavKey) {
      if (options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.toggleInspector();
      }
      return;
    }

    // 1-5 - Abas do Inspector (SEM Alt)
    if (!modKey && !e.shiftKey && !e.altKey && !waitingForNavKey) {
      const num = parseInt(key);
      if (num >= 1 && num <= 5 && options.listActions?.hasList) {
        e.preventDefault();
        options.listActions.setInspectorTab(num);
        return;
      }
    }

    // Escape - Cancelar navegação
    if (key === 'escape') {
      if (waitingForNavKey || navSequence.length > 0) {
        clearNavState();
      }
      return;
    }

    // G - Iniciar navegação
    if (key === 'g' && !modKey && !e.shiftKey && !e.altKey && !waitingForNavKey) {
      e.preventDefault();
      setWaitingForNavKey(true);
      setNavSequence(['g']);
      const timeout = setTimeout(() => clearNavState(), 2000); // 2 segundos para completar
      if (navTimeout) clearTimeout(navTimeout);
      setNavTimeout(timeout);
      return;
    }

    // Sequência de navegação ativa
    if (waitingForNavKey) {
      e.preventDefault();
      
      const newSequence = [...navSequence, key];
      
      // Verificar navegação por perspectiva (G + C/P + letra)
      if (newSequence.length === 3 && newSequence[0] === 'g') {
        const perspectiveKey = `${newSequence[1]}-${newSequence[2]}`;
        if (perspectiveMap[perspectiveKey]) {
          clearNavState();
          
          // Disparar evento para mudar aba se já estiver na página
          const [path, query] = perspectiveMap[perspectiveKey].split('?');
          const tab = new URLSearchParams(query).get('tab');
          
          if (path === '/preferencias') {
            // Preferências usa modal, dispara evento
            window.dispatchEvent(new CustomEvent('open-preferencias', { detail: { tab } }));
          } else {
            setLocation(perspectiveMap[perspectiveKey]);
          }
          return;
        }
      }
      
      // Verificar navegação simples (G + letra)
      if (newSequence.length === 2 && newSequence[0] === 'g') {
        const navKey = newSequence[1];
        
        // Se for P, aguardar terceira tecla para perspectiva (Preferências)
        if (navKey === 'p') {
          setNavSequence(newSequence);
          // Renovar timeout
          const timeout = setTimeout(() => {
            // Se timeout, ir para página padrão
            if (navKey === 'p') setLocation('/preferencias');
            clearNavState();
          }, 1000);
          if (navTimeout) clearTimeout(navTimeout);
          setNavTimeout(timeout);
          return;
        }
        
        // Navegação simples
        if (navigationMap[navKey]) {
          clearNavState();
          setLocation(navigationMap[navKey]);
          return;
        }
      }
      
      // Atualizar sequência se ainda esperando
      if (newSequence.length < 3) {
        setNavSequence(newSequence);
        return;
      }
      
      // Sequência inválida, cancelar
      clearNavState();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping, waitingForNavKey, navSequence, navTimeout, options, setLocation, clearNavState]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (navTimeout) clearTimeout(navTimeout);
    };
  }, [handleKeyDown, navTimeout]);

  return { 
    waitingForNavKey,
    navSequence, // Útil para mostrar feedback visual (ex: "G → C → ?")
    navKeys // Teclas vivas do balão "Ir para:" (derivadas do navigationMap)
  };
}
